import type { PropsWithChildren } from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";

import type { IUserGenderType } from "@/design-system/icons/avatar-icon";
import { FetchGet } from "@/lib/fetch-helper";
import { getLocalStorageItem, setLocalStorageItem } from "@/lib/local-storage-helper";
import { useQueryCustom } from "@/src/query-client-overrides";
import { browserStatics } from "@/src/shared/BrowserStatic";
import { UserLanguageType, urlFormat } from "@Components/Modules/Helpers";
import { showMainLoading } from "@Components/Modules/MainLoading";
import { queryAuthenticationKey } from "@Constants/PortalConstants";
import { User } from "@Routes/UserLinks";

export type LikedType = "brands" | "discounts" | "malls";

export type IAuthContext = {
    userName: string;
    isAuthenticated: boolean;
    canBeMGMSender: boolean;
    genderType: IUserGenderType;
    userId: string;
    discounts: Record<string, boolean>;
    brands: Record<string, boolean>;
    malls: Record<string, boolean>;
    language: UserLanguageType;
    add: (_id: string, _type: LikedType) => void;
    remove: (_id: string, _type: LikedType) => void;
};

type IUserContextActions = {
    refreshUserContext: () => void;
    updateUsername: (_username: string) => void;
};

export const IsAuthenticatedStorageKey = "isAuthenticated";
export const UserAuthenticationContext = createContext<IAuthContext & IUserContextActions>(null);
const userContextEndpoint = urlFormat(User, { action: "UserContext" });

export interface IUserContextResult {
    language: UserLanguageType;
    userName: string;
    isAuthenticated: boolean;
    canBeMGMSender: boolean;
    genderType: IUserGenderType;
    userId: string;
    brandIds: string[];
    discountIds: string[];
    mallIds: string[];
}

export const UserAuthenticationContextProvider = ({
    children,
    userContext,
}: PropsWithChildren<{ preventAuthenticationRefresh: boolean; userContext: IUserContextResult }>) => {
    const { data, refetch } = useQueryCustom({
        queryKey: [queryAuthenticationKey],
        queryFn: () => FetchGet<IUserContextResult>(userContextEndpoint),
        initialData: userContext,
        refetchOnWindowFocus: "always",
        refetchOnReconnect: "always",
    });
    const userAuthRef = useRef(data.isAuthenticated);

    const dataRef = useRef(data);

    useEffect(() => {
        const authValue = getLocalStorageItem(IsAuthenticatedStorageKey);
        if (authValue !== userAuthRef.current) {
            setLocalStorageItem(IsAuthenticatedStorageKey, userContext.isAuthenticated);
        }
    }, [userContext.isAuthenticated]);

    const refreshUserContext = () => {
        showMainLoading();
        window.location.reload();
    };

    const [{ discounts, brands, malls }, setLiked] = useState(() => ({
        brands:
            userContext.brandIds?.reduce<Record<string, boolean>>((acc, id) => {
                acc[id] = true;
                return acc;
            }, {}) || {},
        discounts:
            userContext.discountIds?.reduce<Record<string, boolean>>((acc, id) => {
                acc[id] = true;
                return acc;
            }, {}) || {},
        malls:
            userContext.mallIds?.reduce<Record<string, boolean>>((acc, id) => {
                acc[id] = true;
                return acc;
            }, {}) || {},
    }));

    const remove = (id: string, type: LikedType) => {
        setLiked((prev) => {
            const newLiked = { ...prev };
            newLiked[type] = { ...newLiked[type] };
            delete newLiked[type][id];
            return newLiked;
        });
    };

    const add = (id: string, type: LikedType) => {
        setLiked((prev) => {
            const newLiked = { ...prev };
            newLiked[type] = { ...newLiked[type] };
            newLiked[type][id] = true;
            return newLiked;
        });
    };

    useEffect(() => {
        if (typeof window.gtag !== "undefined") {
            window.gtag("set", { user_id: data.userId });
        }
    }, [data.userId]);

    useEffect(() => {
        dataRef.current = data;
        if (data.language !== browserStatics.language) {
            showMainLoading();
            window.location.reload();
            return;
        }

        const authValue = getLocalStorageItem(IsAuthenticatedStorageKey);
        if (authValue !== userAuthRef.current) {
            setLocalStorageItem(IsAuthenticatedStorageKey, data.isAuthenticated);
        }
    }, [data.language, data.isAuthenticated, data]);

    useEffect(() => {
        const handleStorageChange = (event: Event) => {
            if (event.type === "storage" || event.type === "localStorageChanged") {
                const authValue = getLocalStorageItem(IsAuthenticatedStorageKey);

                if (authValue !== userAuthRef.current) {
                    window.location.reload();
                }
            }
        };
        window.addEventListener("storage", handleStorageChange);
        window.addEventListener("localStorageChanged", handleStorageChange);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
            window.removeEventListener("localStorageChanged", handleStorageChange);
        };
    }, []);

    const updateUsername = (username: string) => {
        if (username !== data.userName) {
            refetch();
        }
    };

    return (
        <UserAuthenticationContext.Provider
            value={{
                ...data,
                refreshUserContext,
                updateUsername,
                discounts,
                brands,
                malls,
                add,
                remove,
            }}
        >
            {children}
        </UserAuthenticationContext.Provider>
    );
};

export const useUserAuthenticationContext = () => useContext(UserAuthenticationContext);

export const useIdLikedContext = (id: string, type: LikedType): [boolean, () => void, () => void] => {
    const context = useContext(UserAuthenticationContext);

    return [context[type][id] || false, () => context.add(id, type), () => context.remove(id, type)];
};

export const useLikedContext = (
    type: LikedType,
): [Record<string, boolean>, (_id: string) => void, (_id: string) => void] => {
    const context = useContext(UserAuthenticationContext);

    return [
        context[type] || {},
        (id: string) => context.add(id, type),
        (id: string) => context.remove(id, type),
    ];
};

export function languageSelector<T>(polishResources: T, englishResources: T) {
    const { language } = browserStatics;

    return resourceSelector(polishResources, englishResources, language);
}

export function resourceSelector<T>(polishResources: T, englishResources: T, language: UserLanguageType) {
    return language === UserLanguageType.English ? englishResources : polishResources;
}
